import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import { BACKEND_URL } from "../../config";
import { Medicine } from "./types";
import { useAuth } from "../../modules/auth/authContext";
import { isBottledMedicine, medicineDoseConfig } from "../../utils/medicineDose";

const medicineTypes = [
  "Tablet", "Capsule", "Syrup", "Suspension", "Drops",
  "Eye Drops", "Ear Drops", "Nasal Drops", "Oral Drops",
  "Ointment", "Cream", "Gel", "Lotion", "Solution",
  "Injection", "Infusion", "Inhaler", "Nebulizer Solution", "Spray",
  "Powder", "Granules", "Sachet", "Suppository", "Pessary",
  "Patch", "Lozenge", "Mouthwash", "Other",
];

const AddNewMedicine: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();
  const catalogMode = location.pathname.startsWith("/services")
    ? { label: "Service", plural: "Services", category: "SERVICE", basePath: "/services" }
    : location.pathname.startsWith("/labs")
      ? { label: "Lab", plural: "Labs", category: "LAB", basePath: "/labs" }
      : { label: "Medicine", plural: "Medicines", category: "MEDICINE", basePath: "/medicines" };

  const medicineData: Medicine | undefined = (location.state as any)?.medicine;
  const isUpdate = Boolean(medicineData?.id);
  const isView = location.state?.view === true || !can("catalog.edit");

  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    redirectTo: undefined as string | undefined,
  });

  const [form, setForm] = useState<Partial<Medicine> & { loose_units?: number | string }>({
    name: "",
    type: catalogMode.category === "MEDICINE" ? "" : catalogMode.category,
    default_meal_instruction: "",
    manufacturer: "",
    brand: "",
    description: "",
    batchNo: "",
    strip_count: 0,
    loose_units: 0,
    strip_cost: 0,
    medicines_per_strip: 0,
    mrpPrice: 0,
    buyingPrice: 0,
    quantity: 0,
    expiryDate: "",
  });

  const isBottle = catalogMode.category === "MEDICINE" && isBottledMedicine(form);
  const usesStrips = /^(tablets?|capsules?|medicine|tab|cap)$/i.test(form.type || "MEDICINE");

  // ----------------------------
  // DATE HANDLING HELPERS
  // ----------------------------
  /*const formatDateForInput = (d?: string) => {
    if (!d) return "";
    const m = d.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (!m) return "";
    return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  };*/

const formatToMonthInput = (d?: string) => {
  if (!d) return "";
  const [day, month, year] = d.split("-");
  return `${year}-${month}`;
};

const formatDateForBackend = (d?: string) => {
  if (!d) return undefined;

  // Case 1: already correct DD-MM-YYYY
  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(d)) return d;

  // Case 2: ISO format YYYY-MM-DD → convert to DD-MM-YYYY
  let m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;

  // Case 3: YYYY-MM → prepend 01 for day
  m = d.match(/^(\d{4})-(\d{2})$/);
  if (m) return `01-${m[2]}-${m[1]}`;

  // Anything else → undefined
  return undefined;
};

const calculateStripStock = (stripCount?: number | string, medicinesPerStrip?: number | string, looseUnits?: number | string) => {
  const strips = Number(stripCount || 0);
  const perStrip = Number(medicinesPerStrip || 0);
  return (strips > 0 && perStrip > 0 ? strips * perStrip : 0) + Number(looseUnits || 0);
};

const calculateUnitCost = (stripCost?: number | string, medicinesPerStrip?: number | string) => {
  const cost = Number(stripCost || 0);
  const perStrip = Number(medicinesPerStrip || 0);
  return cost > 0 && perStrip > 0 ? Number((cost / perStrip).toFixed(2)) : 0;
};

  useEffect(() => {
    if (medicineData) {
      const perStrip = Number(medicineData.medicines_per_strip || 0);
      const stock = Number(medicineData.quantity || 0);
      const fullStrips = perStrip > 0 ? Math.floor(stock / perStrip) : 0;
      setForm({
        ...medicineData,
        strip_count: fullStrips,
        loose_units: stock - fullStrips * perStrip,
        type: medicineData.type || catalogMode.category,
        expiryDate: formatToMonthInput(medicineData.expiryDate),
      });
    }
  }, [medicineData]);

  const handleChange = (k: keyof Medicine, v: any) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const handleStripChange = (k: keyof Medicine | "loose_units", v: any) => {
    setForm((prev) => {
      const next = { ...prev, [k]: v };
      return {
        ...next,
        quantity: calculateStripStock(next.strip_count, next.medicines_per_strip, next.loose_units),
        mrpPrice: calculateUnitCost(next.strip_cost, next.medicines_per_strip),
      };
    });
  };

  // ----------------------------
  // SUBMIT HANDLER
  // ----------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isView) return;

    setLoading(true);
    try {
      const url = isUpdate
        ? `${BACKEND_URL}/api/medicines/${medicineData?.id}`
        : `${BACKEND_URL}/api/medicines/`;

      const method = isUpdate ? "PUT" : "POST";

      const payload = { ...form };
      if (catalogMode.category && catalogMode.category !== "MEDICINE") {
        payload.type = catalogMode.category;
        payload.brand = payload.brand || catalogMode.label;
        payload.manufacturer = payload.manufacturer || "Clinic";
        payload.buyingPrice = Number(payload.buyingPrice || 0);
        payload.quantity = Number(payload.quantity || 0);
        delete payload.strip_count;
        delete payload.loose_units;
        delete payload.strip_cost;
        delete payload.buying_price_per_strip;
        delete payload.medicines_per_strip;
        delete payload.expiryDate;
      }
      if (catalogMode.category === "MEDICINE") {
        if (isBottle) {
          delete payload.loose_units;
          payload.strip_count = 0;
          payload.strip_cost = 0;
          payload.medicines_per_strip = 0;
        }
        delete payload.buying_price_per_strip;
        if (isUpdate) {
          delete payload.buyingPrice;
        } else {
          payload.buyingPrice = 0;
        }
      }
      const formattedExpiry = formatDateForBackend(form.expiryDate as string);
      if (catalogMode.category === "MEDICINE" && formattedExpiry) payload.expiryDate = formattedExpiry;

      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setModal({
          open: true,
          title: "Success",
          message: `${catalogMode.label} ${isUpdate ? "updated" : "created"} successfully`,
          redirectTo: catalogMode.basePath,
        });
      } else {
        setModal({
          open: true,
          title: "Failed",
          message: `Unable to save ${catalogMode.label.toLowerCase()}`,
          redirectTo: catalogMode.basePath
        });
      }
    } catch (err) {
      console.error(err);
      setModal({
        open: true,
        title: "Error",
        message: "Something went wrong",
        redirectTo: catalogMode.basePath
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SidebarLayout
      title={isView ? `View ${catalogMode.label}` : isUpdate ? `Edit ${catalogMode.label}` : `Add New ${catalogMode.label}`}
    >
      <Modal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        onClose={() => {
          const r = modal.redirectTo;
          setModal({ ...modal, open: false });
          if (r) navigate(r);
        }}
      />

      {/* CARD */}
      <div className="bg-surface-light p-6 rounded border max-w-3xl">
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* ROW 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label>
              <div className="text-sm font-medium">Name</div>
              <input
                required
                value={form.name || ""}
                disabled={isView}
                onChange={(e) => handleChange("name", e.target.value)}
                className="w-full p-3 border rounded"
              />
            </label>

            <label>
              <div className="text-sm font-medium">Category</div>
              <input
                value={catalogMode.category || form.type || ""}
                disabled
                onChange={(e) => handleChange("type", e.target.value)}
                className="w-full p-3 border rounded bg-background-light text-subtext-light"
              />
            </label>
          </div>

          {catalogMode.category === "MEDICINE" && (
            <label>
              <div className="text-sm font-medium">Medicine Type</div>
              <select
                required
                disabled={isView}
                value={form.type || ""}
                onChange={(e) => handleChange("type", e.target.value)}
                className="w-full p-3 border rounded"
              >
                <option value="">Select medicine type</option>
                {form.type && !medicineTypes.includes(form.type) && <option value={form.type}>{form.type} (existing type)</option>}
                {medicineTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
              {form.type && <div className="mt-1 text-sm text-subtext-light">
                {medicineDoseConfig(form).custom ? "Consultation dose: enter the prescribed amount and unit." : `Consultation dose: ${medicineDoseConfig(form).plural} per time.`}
              </div>}
            </label>
          )}

          {/* ROW 2 */}
          {catalogMode.category === "MEDICINE" && (
            <label>
              <div className="text-sm font-medium">Default Meal Instruction</div>
              <select
                disabled={isView}
                value={form.default_meal_instruction || ""}
                onChange={(e) => handleChange("default_meal_instruction", e.target.value)}
                className="w-full p-3 border rounded"
              >
                <option value="">No default</option>
                <option value="before_meal">Before meal</option>
                <option value="after_meal">After meal</option>
              </select>
              <div className="mt-1 text-sm text-subtext-light">Automatically selected during consultation. Can be changed for each prescription.</div>
            </label>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label>
              <div className="text-sm font-medium">Supplier</div>
              <input
                value={form.manufacturer || ""}
                disabled={isView}
                onChange={(e) => handleChange("manufacturer", e.target.value)}
                className="w-full p-3 border rounded"
              />
            </label>

            <label>
              <div className="text-sm font-medium">{isBottle ? "MRP per Bottle" : catalogMode.category === "MEDICINE" ? "MRP Price" : "Price"}</div>
              <input
                type="number"
                disabled={(catalogMode.category === "MEDICINE" && !isBottle) || isView}
                value={form.mrpPrice}
                step="0.01"          // ✅ allows decimals
                min="0"              // ✅ no negative values (optional)
                onChange={(e) => {
                  const value = e.target.value === "" ? "" : Number(e.target.value);
                  handleChange("mrpPrice", value);
                }}
                className={`w-full p-3 border rounded ${catalogMode.category === "MEDICINE" && !isBottle ? "bg-background-light text-subtext-light" : ""}`}
              />
            </label>
            {false && catalogMode.category === "MEDICINE" && <label>
              <div className="text-sm font-medium">Buying Price</div>
              <input
                type="number"
                disabled
                value={form.buyingPrice}
                step="0.01"          // ✅ allows decimals
                min="0"              // ✅ no negative values (optional)
                onChange={(e) => {
                  const value = e.target.value === "" ? "" : Number(e.target.value);
                  handleChange("buyingPrice", value);
                }}
                className="w-full p-3 border rounded bg-background-light text-subtext-light"
              />
            </label>}


          </div>

          {/* ROW 3 */}
          {catalogMode.category === "MEDICINE" && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label>
              <div className="text-sm font-medium">Brand</div>
              <input
                value={form.brand || ""}
                disabled={isView}
                onChange={(e) => handleChange("brand", e.target.value)}
                className="w-full p-3 border rounded"
              />
            </label>

            <label>
              <div className="text-sm font-medium">Batch Number</div>
              <input
                type="text"
                value={form.batchNo || ""}
                disabled={isView}
                onChange={(e) => handleChange("batchNo", e.target.value)}
                className="w-full p-3 border rounded"
              />
            </label>
          </div>}

          {/* ROW 4 */}
          {catalogMode.category === "MEDICINE" && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label>
              <div className="text-sm font-medium">{isBottle ? "Stock (Bottles)" : "Stock"}</div>
              <input
                type="number"
                value={form.quantity}
                disabled={!isBottle || isView}
                min="0"
                step="1"
                onChange={(e) => handleChange("quantity", Number(e.target.value))}
                className={`w-full p-3 border rounded ${!isBottle ? "bg-background-light text-subtext-light" : ""}`}
              />
            </label>

            <label>
              <div className="text-sm font-medium">Expiry Date</div>
              <input
                type="month"
                required
                disabled={isView}
                value={form.expiryDate}
                onChange={(e) => handleChange("expiryDate", e.target.value)}
                className="w-full p-3 border rounded"
              />
            </label>
          </div>}

          {catalogMode.category === "MEDICINE" && !isBottle && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label>
                <div className="text-sm font-medium">{usesStrips ? "Strip Count" : "Pack Count"}</div>
                <input
                  type="number"
                  min="0"
                  step="1"
                  disabled={isView}
                  value={form.strip_count}
                  onChange={(e) => handleStripChange("strip_count", e.target.value === "" ? "" : Number(e.target.value))}
                  className="w-full p-3 border rounded"
                />
              </label>

              <label>
                <div className="text-sm font-medium">{usesStrips ? "Cost per Strip in MRP" : "MRP per Pack"}</div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={isView}
                  value={form.strip_cost}
                  onChange={(e) => handleStripChange("strip_cost", e.target.value === "" ? "" : Number(e.target.value))}
                  className="w-full p-3 border rounded"
                />
              </label>

              {false && <label>
                <div className="text-sm font-medium">Buying Price per Strip</div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={isView}
                  value={form.buying_price_per_strip}
                  onChange={(e) => handleStripChange("buying_price_per_strip", e.target.value === "" ? "" : Number(e.target.value))}
                  className="w-full p-3 border rounded"
                />
              </label>}

              <label>
                <div className="text-sm font-medium">{usesStrips ? "Medicines per Strip" : "Units per Pack"}</div>
                <input
                  type="number"
                  min="0"
                  disabled={isView}
                  value={form.medicines_per_strip}
                  onChange={(e) => handleStripChange("medicines_per_strip", e.target.value === "" ? "" : Number(e.target.value))}
                  className="w-full p-3 border rounded"
                />
                {!usesStrips && <div className="mt-1 text-sm text-subtext-light">Enter 1 for an individually sold tube, bottle, or device.</div>}
              </label>
              <label>
                <div className="text-sm font-medium">{usesStrips ? "Loose tablets / capsules" : "Loose units"}</div>
                <input
                  type="number"
                  min="0"
                  step="1"
                  disabled={isView}
                  value={form.loose_units ?? 0}
                  onChange={(e) => handleStripChange("loose_units", e.target.value === "" ? "" : Number(e.target.value))}
                  className="w-full p-3 border rounded"
                />
                <div className="mt-1 text-sm text-subtext-light">Enter full {usesStrips ? "strips" : "packs"} above and extra individual units here. Example: 4 × 15 + 10 loose = 70.</div>
              </label>
            </div>
          )}

          {/* ROW 5 */}
          <label>
            <div className="text-sm font-medium">Description</div>
            <input
              value={form.description || ""}
              disabled={isView}
              onChange={(e) => handleChange("description", e.target.value)}
              className="w-full p-3 border rounded"
            />
          </label>

          {/* ACTIONS */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate(catalogMode.basePath)}
              className="px-4 py-2 rounded bg-background-light"
            >
              Back
            </button>

            {!isView && (
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 rounded bg-primary text-white"
              >
                {loading ? "Saving..." : isUpdate ? "Update" : "Save"}
              </button>
            )}
          </div>
        </form>
      </div>
    </SidebarLayout>
  );
};

export default AddNewMedicine;
